import { useEffect, useId, useRef, useState } from 'react'
import { DateField } from '@astro/date-field'
import { apiErrorMessage, istDate } from './trackingUtils'

const MAX_DAYS_AHEAD = 3

/**
 * "Reschedule delivery" after a failed attempt: preferred date (today to 3
 * days ahead), a phone the courier can reach, a landmark, and a note. Only a
 * re-attempt can be asked for here (D5). `onSubmit(body)` returns the API's
 * promise; the dialog closes on success and shows plain copy on failure.
 */
export default function NdrResponseModal({ open, onClose, onSubmit }) {
  const titleId = useId()
  const dialogRef = useRef(null)
  const [form, setForm] = useState({ preferred_date: '', phone: '', address_note: '', comment: '' })
  const [errors, setErrors] = useState({})
  const [failure, setFailure] = useState('')
  const [saving, setSaving] = useState(false)
  // Read through a ref so a new onClose each render doesn't re-run the effect (and refocus).
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    if (!open) return undefined
    const opener = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialogRef.current?.querySelector('input, textarea, button')?.focus()
    const onKey = (e) => {
      if (e.key === 'Escape') closeRef.current()
      // Keep Tab inside the dialog.
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = [...dialogRef.current.querySelectorAll('button, input, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => !el.disabled)
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last?.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first?.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
      opener?.focus?.()
    }
  }, [open])

  if (!open) return null

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const submit = async (e) => {
    e.preventDefault()
    const next = {}
    const digits = form.phone.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '')
    if (form.phone && !/^[6-9]\d{9}$/.test(digits)) next.phone = 'Enter a 10-digit mobile number.'
    if (form.address_note.length > 200) next.address_note = 'Keep it to 200 characters.'
    if (form.comment.length > 500) next.comment = 'Keep it to 500 characters.'
    setErrors(next)
    if (Object.keys(next).length > 0) return
    setSaving(true)
    setFailure('')
    try {
      await onSubmit({
        action: 'reattempt',
        ...(form.preferred_date ? { preferred_date: form.preferred_date } : {}),
        ...(digits ? { phone: digits } : {}),
        ...(form.address_note.trim() ? { address_note: form.address_note.trim() } : {}),
        ...(form.comment.trim() ? { comment: form.comment.trim() } : {}),
      })
      onClose()
    } catch (err) {
      setFailure(apiErrorMessage(err, "We couldn't send your request. Please try again."))
    } finally {
      setSaving(false)
    }
  }

  const input = 'w-full h-12 px-4 rounded-xl border border-border bg-white text-sm text-primary normal-case focus:outline-none focus:border-secondary'

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-primary/40" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-xl p-6 sm:p-8 normal-case"
      >
        <div className="flex items-start justify-between gap-4 mb-2">
          <h2 id={titleId} className="text-lg font-heading text-primary tracking-wider uppercase">
            Reschedule delivery
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="w-9 h-9 -mr-2 -mt-1 rounded-full text-primary/50 hover:text-primary hover:bg-primary/5">
            <i className="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </div>
        <p className="text-sm text-primary/60 mb-6">The courier will try again. Everything below is optional - add what helps them reach you.</p>

        <form onSubmit={submit} noValidate className="space-y-5">
          <Field label="Preferred delivery date" hint={`Within the next ${MAX_DAYS_AHEAD} days`}>
            {(id) => (
              <DateField id={id} name="preferred_date" value={form.preferred_date} onChange={set('preferred_date')} min={istDate(0)} max={istDate(MAX_DAYS_AHEAD)} className={input} />
            )}
          </Field>
          <Field label="Phone for the courier" error={errors.phone}>
            {(id, describedBy) => (
              <input id={id} type="tel" inputMode="numeric" autoComplete="tel" value={form.phone} onChange={set('phone')} aria-invalid={Boolean(errors.phone)} aria-describedby={describedBy} className={input} placeholder="10-digit mobile" />
            )}
          </Field>
          <Field label="Landmark or address note" error={errors.address_note}>
            {(id, describedBy) => (
              <input id={id} type="text" value={form.address_note} onChange={set('address_note')} maxLength={200} aria-invalid={Boolean(errors.address_note)} aria-describedby={describedBy} className={input} placeholder="e.g. Opposite the temple gate" />
            )}
          </Field>
          <Field label="Note for the courier" error={errors.comment}>
            {(id, describedBy) => (
              <textarea id={id} rows={3} value={form.comment} onChange={set('comment')} maxLength={500} aria-invalid={Boolean(errors.comment)} aria-describedby={describedBy} className={`${input} h-auto py-3`} />
            )}
          </Field>

          {failure && (
            <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              {failure}
            </p>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <button type="button" onClick={onClose} className="h-12 px-6 rounded-xl border border-primary/20 text-primary text-xs font-bold tracking-[0.15em] uppercase hover:border-secondary hover:text-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="h-12 px-6 grow rounded-xl bg-primary text-white text-xs font-bold tracking-[0.15em] uppercase hover:bg-secondary disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {saving && <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>}
              {saving ? 'Sending…' : 'Request re-delivery'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({ label, hint, error, children }) {
  const id = useId()
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined
  return (
    <div>
      <label htmlFor={id} className="block text-xs text-primary/60 tracking-wider mb-1.5">
        {label}
      </label>
      {children(id, describedBy)}
      {hint && !error && (
        <p id={`${id}-hint`} className="mt-1 text-[11px] text-primary/40">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  )
}
